import { useEffect, useMemo, useState } from "react";
import { Modal } from "antd";
import { Drawer } from "../../../components/drawer/drawer";
import { DrawerHeader } from "../../../components/drawer/drawer-header";
import BlockComponent from "../../../components/block/block";
import Table from "../../../components/table/table";
import useNotification from "../../../components/notification/notification";
import { useProjectNavigate } from "../../../common/utils/useProjectNavigate";
import ApiRoles from "../../../api/roles";
import RoleForm, { Field, Section, Value } from "./role-form";
import RoleDeleteModal from "./role-delete-modal";
import {
  changesOf,
  CONFIRM_MODAL,
  failureMessage,
  fieldErrors,
  permissionDiff,
  sensitiveIn,
  utc,
  validationError,
} from "./role-edit";

const MEMBER_COLUMNS = [
  { title: "Project", dataIndex: "title", key: "title" },
  { title: "Members", dataIndex: "members", key: "members", width: 120 },
];

const when = (date, person) => {
  if (!date) return "—";
  return person?.name ? `${utc(date)} · ${person.name}` : utc(date);
};

const draftOf = (role) => ({
  name: role.name,
  description: role.description || "",
  siteScoping: role.siteScoping,
  permissions: role.permissions,
});

const List = ({ title, items }) => (items.length ? (
  <div className="role-confirm__list">
    <p>{title}</p>
    <ul>{items.map((item) => <li key={item}>{item}</li>)}</ul>
  </div>
) : null);

/**
 * LF360-2045: one role — what it is, what it may do, and who holds it.
 * Members are counted per project; a project links to its project users list,
 * filtered by the role where that list can filter by it.
 *
 * LF360-2047 / 2048 / 2049: Organization and System admins edit it here, put a
 * built-in role — or all three — back to the recommended defaults, and delete
 * a custom one. Every change is confirmed first and sends the version the
 * card was opened with.
 */
const RoleCard = ({
  role,
  projects = [],
  catalog,
  isOpen,
  close,
  roles = [],
  builtInVersions = {},
  onChanged,
  onDeleted,
}) => {
  const navigate = useProjectNavigate();
  const { openNotification } = useNotification();

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [confirm, setConfirm] = useState(null); // "save" | "restore" | "restoreAll" | "delete"
  const [busy, setBusy] = useState(false);
  // Fields are marked once a save was refused, and clear as they are fixed.
  const [tried, setTried] = useState(false);

  // A different role, or the same one reloaded, starts out read-only.
  useEffect(() => {
    setIsEditing(false);
    setDraft(null);
    setConfirm(null);
    setTried(false);
  }, [role?.id, role?.version]);

  const members = projects.reduce((sum, project) => sum + project.members, 0);
  const isBuiltIn = role?.type === "builtIn";
  const changes = useMemo(() => (role && draft ? changesOf(role, draft) : {}), [role, draft]);
  const hasChanges = Object.keys(changes).length > 0;

  const openMembers = (project) => {
    // By id: the users list filters by role record, custom roles included.
    navigate(`/administration/project-users?projectRole=${encodeURIComponent(role.id)}`, {
      projectCode: project.code,
    });
  };

  const fail = (error) => {
    openNotification({ type: "error", message: <p>{failureMessage(error, "Unable to save the role")}</p> });
  };

  const startEditing = () => {
    setDraft(draftOf(role));
    setIsEditing(true);
  };

  // Closing the editor drops what was typed: the card shows the role as saved.
  const stopEditing = () => {
    setIsEditing(false);
    setDraft(null);
    setTried(false);
  };

  const askToSave = () => {
    const problem = validationError(draft);
    if (problem) {
      setTried(true);
      openNotification({ type: "error", message: <p>{problem}</p> });
      return;
    }
    setConfirm("save");
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await ApiRoles.updateRole(role.id, { version: role.version, ...changes });
      openNotification({ type: "success", message: <p>The role "{data.role.name}" was edited</p> });
      setConfirm(null);
      onChanged?.([data.role]);
    } catch (error) {
      setConfirm(null);
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setBusy(true);
    try {
      const all = confirm === "restoreAll";
      const { data } = all
        ? await ApiRoles.restoreAllDefaults(builtInVersions)
        : await ApiRoles.restoreDefaults(role.id, role.version);
      const restored = all ? data.roles : [data.role];
      openNotification({
        type: "success",
        message: (
          <p>
            {all
              ? "Recommended defaults restored for all built-in roles"
              : `Recommended defaults restored for "${data.role.name}"`}
          </p>
        ),
      });
      setConfirm(null);
      onChanged?.(restored);
    } catch (error) {
      setConfirm(null);
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  // Offered while editing too: that is where an admin who switched the wrong
  // permissions off looks for the way back. A restore replaces the role, which
  // closes the editor and drops what was typed.
  const menu = isBuiltIn
    ? {
      items: [
        { key: "restore", label: "Restore recommended defaults", onClick: () => setConfirm("restore") },
        { key: "restore-all", label: "Restore defaults for all built-in roles", onClick: () => setConfirm("restoreAll") },
      ],
    }
    : undefined;

  const diff = isEditing ? permissionDiff(catalog, role.permissions, draft.permissions) : null;
  const sensitive = isEditing ? sensitiveIn(catalog, draft.permissions) : [];

  // Draft, story 5: a custom role's scoping is locked while anybody holds it;
  // a built-in role's is fixed.
  const scopingLock = isBuiltIn
    ? "Site scoping of a built-in role cannot be changed"
    : members > 0
      ? `Reassign the ${members} members first, or create a new role`
      : null;

  return (
    <Drawer open={isOpen} onClose={close} size={800}>
      <DrawerHeader
        title={<p className="life-fact-title">Role</p>}
        onClose={close}
        isEditing={isEditing}
        onEdit={role ? startEditing : undefined}
        onCancel={stopEditing}
        onSave={askToSave}
        saveDisabled={!hasChanges}
        isLoading={busy}
        menu={menu}
        // Draft, story 7: custom roles only.
        onDelete={role && !isBuiltIn && !isEditing ? () => setConfirm("delete") : undefined}
      />
      {role && (
        <div style={{ paddingBottom: 48 }}>
          <RoleForm
            catalog={catalog}
            draft={isEditing ? draft : draftOf(role)}
            setDraft={setDraft}
            readonly={!isEditing}
            errors={isEditing && tried ? fieldErrors(draft) : {}}
            scopingLock={scopingLock}
          >
            <Field label="Type"><Value>{isBuiltIn ? "Built-in" : "Custom"}</Value></Field>
            {!isEditing && (
              <>
                <Field label="Created"><Value>{when(role.createdAt, role.createdBy)}</Value></Field>
                <Field label="Last modified"><Value>{when(role.updatedAt, role.updatedBy)}</Value></Field>
              </>
            )}
          </RoleForm>

          <Section label="Members">
            {projects.length ? (
              <BlockComponent>
                <Table
                  columns={MEMBER_COLUMNS}
                  data={projects.map((project) => ({ ...project, id: project.projectId }))}
                  onHandleRowClick={openMembers}
                />
              </BlockComponent>
            ) : (
              <span>Nobody holds this role yet.</span>
            )}
          </Section>
        </div>
      )}

      <Modal
        open={confirm === "save"}
        title={`Save the role "${draft?.name?.trim() || role?.name}"?`}
        okText="Save"
        onOk={save}
        onCancel={() => setConfirm(null)}
        confirmLoading={busy}
        destroyOnHidden
        {...CONFIRM_MODAL}
      >
        {diff && (
          <div className="role-confirm">
            <List title="Permissions added" items={diff.added} />
            <List title="Permissions removed" items={diff.removed} />
            <p>
              {members} users in {projects.length} projects will be affected. Applies to all
              projects in this organization.
            </p>
            {sensitive.length > 0 && (
              <p className="role-confirm__sensitive">
                This role includes sensitive permissions: {sensitive.join(", ")}. Continue?
              </p>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={confirm === "restore" || confirm === "restoreAll"}
        title="Restore recommended defaults"
        okText="Restore"
        onOk={restore}
        onCancel={() => setConfirm(null)}
        confirmLoading={busy}
        destroyOnHidden
        {...CONFIRM_MODAL}
      >
        <p>
          {confirm === "restoreAll"
            ? "Restore the recommended defaults for all built-in roles?"
            : `Restore the recommended defaults for ${role?.name}?`}
        </p>
        <p>Users with this role will gain or lose access immediately.</p>
      </Modal>

      {role && !isBuiltIn && (
        <RoleDeleteModal
          open={confirm === "delete"}
          role={role}
          roles={roles}
          catalog={catalog}
          onCancel={() => setConfirm(null)}
          onDeleted={(result) => {
            setConfirm(null);
            onDeleted?.(role, result);
          }}
        />
      )}
    </Drawer>
  );
};

export default RoleCard;
